import React from 'react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { fetchQrCode } from '@/services/entry-point';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { QrCode } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
const defaultStyle = {
    size: 300,
    margin: 4,
    errorCorrectionLevel: 'H',
    roundBlockSizeMode: 'margin',
    foregroundColor: '#000000',
    backgroundColor: '#ffffff',
    format: 'png',
};
export const QrCustomizeDialog = ({ entryPoint, isIcon }) => {
    const [style, setStyle] = React.useState(defaultStyle);
    const [open, setOpen] = React.useState(false);
    const { data, isPending } = useQuery({
        queryKey: ['entry-point-qr-code', entryPoint.publicId, style],
        queryFn: () => fetchQrCode({
            publicId: entryPoint.publicId,
            params: style,
        }),
        enabled: open,
    });
    return (<Dialog open={open} onOpenChange={(open) => {
            if (open) {
                setStyle(defaultStyle);
            }
            setOpen(open);
        }}>
            <DialogTrigger asChild>
                {isIcon ? (<Tooltip>
                        <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => setOpen((prevState) => !prevState)}>
                                <QrCode />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Generate Qr</p>
                        </TooltipContent>
                    </Tooltip>) : (<Button variant="ghost" size="sm">
                        <QrCode />
                        Generate Qr
                    </Button>)}
            </DialogTrigger>
            <DialogContent onInteractOutside={(e) => {
            e.preventDefault();
        }}>
                <DialogTitle>Generate QR Code for {entryPoint.name}</DialogTitle>
                <DialogDescription>
                    Customize the QR code for {entryPoint.name} by selecting a style and format.
                </DialogDescription>
                <div className="flex gap-2">
                    <div className="flex flex-col gap-2">
                        <p>QR Customize Dialog for {entryPoint.name}</p>
                        {isPending ? (<p>Loading QR code...</p>) : data ? (<img src={data} alt="QR Code Preview"/>) : (<p>No QR code available.</p>)}
                    </div>
                    <div className="space-y-2 w-full">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Foreground</Label>
                                <input type="color" value={style.foregroundColor} onChange={(e) => setStyle((prevState) => ({ ...prevState, foregroundColor: e.target.value }))} className="h-10 w-full"/>
                            </div>

                            <div>
                                <Label>Background</Label>
                                <input type="color" value={style.backgroundColor} onChange={(e) => setStyle((prevState) => ({ ...prevState, backgroundColor: e.target.value }))} className="h-10 w-full"/>
                            </div>

                            <div>
                                <Label>Size</Label>
                                <input type="number" value={style.size} onChange={(e) => setStyle((prevState) => ({ ...prevState, size: Number(e.target.value) }))} className="w-full rounded border px-2 py-1"/>
                            </div>

                            <div>
                                <Label>Error Correction</Label>
                                <Select value={style.errorCorrectionLevel} onValueChange={(value) => setStyle((prevState) => ({
            ...prevState,
            errorCorrectionLevel: value,
        }))}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="L">Low</SelectItem>
                                        <SelectItem value="M">Medium</SelectItem>
                                        <SelectItem value="Q">Quartile</SelectItem>
                                        <SelectItem value="H">High</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label>Round Block Size</Label>
                                <Select value={style.roundBlockSizeMode} onValueChange={(value) => setStyle((prevState) => ({
            ...prevState,
            roundBlockSizeMode: value,
        }))}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        <SelectItem value="margin">Margin</SelectItem>
                                        <SelectItem value="fit">Fit</SelectItem>
                                        <SelectItem value="enlarge">Enlarge</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Download Format</Label>
                                <Select value={style.format} onValueChange={(value) => setStyle((prevState) => ({
            ...prevState,
            format: value,
        }))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={style.format}/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="svg">SVG</SelectItem>
                                        <SelectItem value="png">PNG</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button type="button" asChild>
                        <a href={`/api/v1/en/entry-points/${entryPoint.publicId}/qr-code?${new URLSearchParams({
            size: style.size.toString(),
            margin: style.margin.toString(),
            errorCorrectionLevel: style.errorCorrectionLevel,
            foregroundColor: style.foregroundColor,
            backgroundColor: style.backgroundColor,
            roundBlockSizeMode: style.roundBlockSizeMode,
            format: style.format,
            download: 'true',
        }).toString()}`} target="_blank" rel="noreferrer">
                            Download QR Code
                        </a>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
};
